import React from 'react';
import { useT } from './lang.js';

/**
 * İskelet yükleme.
 *
 * NEDEN: "Yükleniyor…" yazısı boş bir ekranda tek başına duruyordu; sayfa
 * gelince de düzen bir anda zıplıyordu. İskelet, gelecek içeriğin KALIBINI
 * silik olarak çizer: kasiyer tabloyu mu ızgarayı mı beklediğini bilir ve
 * içerik yerine oturduğunda hiçbir şey yerinden oynamaz.
 *
 * Kurallar:
 *  · Kalıp sayfanın GERÇEK düzenine benzer (sütun sayısı, kart sırası) —
 *    yoksa iskelet kendisi bir zıplama kaynağı olur.
 *  · Ekran okuyucu için tek bir `role="status"` + gizli "Yükleniyor" metni
 *    yeter; onlarca boş kutuyu okumasının anlamı yok (`aria-hidden`).
 *  · Hareket `prefers-reduced-motion` ile kapanır (styles.css).
 */

/** Tek silik çubuk. Ölçüler piksel/yüzde olarak doğrudan verilir. */
export function Sk({ w = '100%', h = 13, r, style }) {
  return <span className="sk" style={{ width: w, height: h, ...(r ? { borderRadius: r } : null), ...style }} />;
}

/** Kart başlığı hizasında bir çubuk. */
function Head({ w = '34%' }) {
  return <div className="sk-head"><Sk w={w} h={16} /></div>;
}

/** Tablo satırları: sütun genişlikleri gerçek tablolara yakın tutulur. */
export function SkRows({ n = 6, cols = ['40%', '18%', '16%', '14%'] }) {
  return (
    <div className="sk-rows">
      {Array.from({ length: n }, (_, i) => (
        <div className="sk-row" key={i}>
          {cols.map((w, j) => (
            // Satırlar birebir aynı olmasın: ilk YÜZDE sütunu sırayla kısalır.
            // Piksel sütuna (onay kutusu gibi) dokunulmaz — sıfıra iniyordu.
            <Sk key={j} w={j === 0 && String(w).endsWith('%') ? `calc(${w} - ${(i % 3) * 4}%)` : w} />
          ))}
        </div>
      ))}
    </div>
  );
}

const BOXES = (n, cls) => Array.from({ length: n }, (_, i) => <span className={`sk ${cls}`} key={i} />);

/**
 * Sayfa kalıpları. Her ekran kendi şeklini seçer:
 *   table  — başlık + satırlar (personel, yazıcılar, hesaplar…)
 *   form   — etiket/kutu çiftleri (ayarlar, paket)
 *   grid   — masa ızgarası (salon)
 *   tiles  — kutular + tablo (tahsilat)
 *   menu   — akordeon kartları
 *   bill   — özet + kalemler + düğme tahtası
 *   board  — mutfak sütunları
 */
export default function PageSkeleton({ shape = 'table' }) {
  const t = useT();
  return (
    <>
      <span className="sr-only" role="status">{t('loading')}</span>
      <div aria-hidden="true">{SHAPES[shape] ? SHAPES[shape]() : SHAPES.table()}</div>
    </>
  );
}

const SHAPES = {
  table: () => (
    <div className="card">
      <Head />
      <SkRows n={6} />
    </div>
  ),

  form: () => (
    <>
      <div className="card">
        <Head w="28%" />
        <div className="grid2">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i}><Sk w="42%" h={10} /><Sk h={34} r={8} style={{ marginTop: 6 }} /></div>
          ))}
        </div>
      </div>
      <div className="card">
        <Head w="22%" />
        <SkRows n={5} cols={['18px', '46%']} />
      </div>
    </>
  ),

  grid: () => (
    <>
      <div className="card">
        <div className="sk-row" style={{ borderBottom: 0, padding: 0 }}>
          <Sk w="18%" h={16} /><Sk w="26%" h={12} />
          <span className="spacer" /><Sk w={38} h={32} r={8} />
        </div>
      </div>
      <div className="card">
        <Head w="20%" />
        <div className="sk-grid">{BOXES(8, 'sk-tile')}</div>
      </div>
    </>
  ),

  tiles: () => (
    <>
      <div className="card">
        <Head w="24%" />
        <div className="sk-kpis">{BOXES(4, 'sk-kpi')}</div>
      </div>
      <div className="card">
        <Head w="30%" />
        <SkRows n={4} />
      </div>
    </>
  ),

  menu: () => (
    <>
      <div className="card">
        <Head w="26%" />
        <SkRows n={2} cols={['30%', '22%', '14%']} />
      </div>
      {[0, 1, 2].map((i) => (
        <div className="card" key={i}>
          <div className="sk-row" style={{ borderBottom: 0, padding: 0 }}>
            <Sk w={22} h={22} r={11} /><Sk w="24%" h={15} />
            <span className="spacer" /><Sk w={70} h={28} r={8} />
          </div>
          <Sk w="72%" h={11} style={{ marginTop: 12 }} />
        </div>
      ))}
    </>
  ),

  bill: () => (
    <>
      <div className="card">
        <div className="sk-row" style={{ borderBottom: 0, padding: 0 }}>
          <Sk w="30%" h={17} /><Sk w={90} h={20} r={999} />
          <span className="spacer" /><Sk w={160} h={34} r={8} />
        </div>
        <Sk w="62%" h={12} style={{ marginTop: 14 }} />
      </div>
      <div className="card">
        <Head w="18%" />
        <SkRows n={5} cols={['18px', '38%', '14%', '14%']} />
      </div>
      <div className="card">
        <Head w="14%" />
        <div className="sk-btns">{BOXES(3, 'sk-pay')}</div>
        <Sk w="20%" h={10} style={{ margin: '18px 0 8px' }} />
        <div className="sk-btns">{BOXES(4, 'sk-pay')}</div>
      </div>
    </>
  ),

  board: () => (
    <div className="sk-board">
      {[0, 1, 2].map((i) => (
        <div className="card" key={i}>
          <Head w="40%" />
          <div className="sk-stack">{BOXES(3 - (i % 2), 'sk-ticket')}</div>
        </div>
      ))}
    </div>
  ),
};
